import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Phone,
    Mail,
    ShieldCheck,
    GraduationCap,
    Compass,
    Anchor,
    Award,
    Users,
    CalendarCheck,
    CheckCircle2,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import { COMPANY } from "@/lib/compliance";
import useSeo from "@/lib/useSeo";

const GEORGE_HEADSHOT = `${process.env.REACT_APP_BACKEND_URL}/api/static/team/george-goldman.png`;
const ENDURANCE_SHIP = `${process.env.REACT_APP_BACKEND_URL}/api/static/leadership/endurance-ship.png`;

const CREDENTIAL_BADGES = [
    { icon: GraduationCap, label: "MBA" },
    { icon: Award, label: "CFP\u00AE" },
    { icon: Compass, label: "30+ Years Industry Experience" },
    {
        icon: ShieldCheck,
        label: "Multi-State Licensed Insurance Professional",
    },
];

const WHY_PILLARS = [
    {
        icon: ShieldCheck,
        title: "Trustworthy Guidance",
        body: "Independent advice focused on the client's best interests.",
    },
    {
        icon: Compass,
        title: "Broad Market Access",
        body: "Access to leading insurance carriers and specialized coverage solutions.",
    },
    {
        icon: Users,
        title: "Personal Service",
        body: "Responsive support and personalized attention before, during, and after the sale.",
    },
];

export default function LeadershipPage() {
    useSeo({
        title: "Meet Our Leadership Team",
        description:
            "George Goldman, MBA, CFP® leads Endurance Casualty Insurance Agency. 30+ years of industry experience guiding personal, business, life, health, and cyber clients across NY, NJ, PA, CT, NC, FL, and TX.",
        canonicalPath: "/leadership",
    });

    return (
        <main
            data-testid="page-leadership"
            className="relative min-h-screen bg-brand-bg text-brand-ivory"
        >
            <PageHeader />

            {/* ============= HERO ============= */}
            <section
                data-testid="leadership-hero"
                aria-labelledby="leadership-hero-heading"
                className="relative overflow-hidden border-b border-[rgba(212,175,55,0.18)]"
            >
                {/* Layered navy-blue + charcoal gradient backdrop with a soft
                    gold glow — nods to maritime palette without breaking the
                    site's dark+gold aesthetic. */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(20,38,72,0.55),rgba(5,7,10,0)_60%),linear-gradient(180deg,#0a1426_0%,#070b14_45%,#05070a_100%)]"
                />
                <div
                    aria-hidden="true"
                    className="absolute -top-24 right-[-10%] h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),rgba(212,175,55,0)_70%)] blur-2xl"
                />
                {/* Compass-rose watermark — pure decoration */}
                <Compass
                    aria-hidden="true"
                    className="absolute left-[-60px] bottom-[-60px] h-[260px] w-[260px] text-brand-gold opacity-[0.05]"
                    strokeWidth={0.6}
                />

                <div className="relative mx-auto max-w-[1200px] px-6 py-20 md:px-10 md:py-28">
                    <p className="text-[11px] uppercase tracking-[0.45em] text-brand-goldSoft">
                        Meet Our Leadership Team
                    </p>
                    <h1
                        id="leadership-hero-heading"
                        className="mt-5 max-w-3xl font-serif text-4xl leading-[1.05] text-brand-ivory sm:text-5xl lg:text-6xl"
                    >
                        Leadership Built on{" "}
                        <span className="text-brand-gold2">Experience</span>,{" "}
                        <span className="text-brand-gold2">Integrity</span>,
                        and{" "}
                        <span className="text-brand-gold2">Service</span>.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg leading-relaxed text-brand-ivory/85 md:text-xl">
                        Dedicated professionals committed to helping
                        individuals, families, and businesses protect what
                        they have worked hard to build.
                    </p>
                    <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                        <Link
                            to="/contact"
                            data-testid="leadership-hero-contact"
                            className="inline-flex items-center justify-center gap-3 rounded-full border border-brand-gold bg-brand-gold px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-brand-bg shadow-[0_18px_36px_-18px_rgba(212,175,55,0.6)] transition-all hover:bg-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg active:scale-[0.98]"
                        >
                            Contact Us
                            <ArrowRight aria-hidden="true" className="h-4 w-4" />
                        </Link>
                        <Link
                            to="/#inquiry"
                            data-testid="leadership-hero-quote"
                            className="inline-flex items-center justify-center gap-3 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                        >
                            Request a Quote
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============= THE ENDURANCE STORY ============= */}
            <section
                data-testid="leadership-story"
                aria-labelledby="leadership-story-heading"
                className="relative overflow-hidden border-b border-[rgba(212,175,55,0.12)] bg-[#06080c]"
            >
                {/* Anchor watermark */}
                <Anchor
                    aria-hidden="true"
                    className="pointer-events-none absolute right-[-40px] top-12 h-[280px] w-[280px] text-brand-gold opacity-[0.04]"
                    strokeWidth={0.6}
                />
                <div className="relative mx-auto grid max-w-[1200px] gap-12 px-6 py-20 md:grid-cols-[1fr_1.1fr] md:gap-16 md:px-10 md:py-24">
                    {/* Image / visual column */}
                    <div className="relative">
                        <div
                            data-testid="leadership-story-image"
                            className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.3)] bg-[#05070a] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)]"
                        >
                            <img
                                src={ENDURANCE_SHIP}
                                alt="Painting of the Endurance — Sir Ernest Shackleton's expedition ship — sailing through icy seas under stormy skies"
                                className="h-full w-full object-cover"
                                loading="eager"
                            />
                            {/* Subtle bottom gradient so the caption is legible
                                over the dark portion of the painting */}
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#05070a]/85 via-[#05070a]/40 to-transparent"
                            />
                            <div className="absolute inset-x-6 bottom-5 text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                Endurance &mdash; The Shackleton Expedition
                            </div>
                        </div>
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                            The Endurance Story
                        </p>
                        <h2
                            id="leadership-story-heading"
                            className="mt-4 font-serif text-3xl leading-tight text-brand-ivory md:text-4xl"
                        >
                            Why We Chose the Name{" "}
                            <span className="text-brand-gold2">Endurance</span>
                        </h2>
                        <div className="gold-hairline my-7 w-24" />
                        <p className="text-base leading-relaxed text-brand-ivory/85">
                            {COMPANY.shortName} Insurance Agency was founded
                            for clients and institutions seeking something
                            increasingly difficult to find: reliable
                            guidance from knowledgeable insurance
                            professionals who place their clients&apos;
                            interests first.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-brand-ivory/85">
                            The agency draws inspiration from the remarkable
                            leadership of Sir Ernest Shackleton and the
                            historic Endurance expedition. Faced with
                            extraordinary challenges, Shackleton earned the
                            trust and loyalty of his crew through
                            perseverance, integrity, leadership, and an
                            unwavering commitment to those who depended upon
                            him.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-brand-ivory/85">
                            These same principles guide Endurance Casualty
                            Insurance Agency today. We believe insurance is
                            more than a policy &mdash; it is a relationship
                            built on trust, service, accountability, and
                            long-term commitment to our clients.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-brand-ivory/85">
                            Whether protecting a family, a business, a home,
                            or a lifetime of achievement, our mission remains
                            the same:
                        </p>
                        <p className="mt-5 font-serif text-2xl italic leading-snug text-brand-gold2">
                            &ldquo;Protecting What You Have Built.&rdquo;
                        </p>
                    </div>
                </div>
            </section>

            {/* ============= GEORGE GOLDMAN PROFILE ============= */}
            <section
                data-testid="leadership-george-profile"
                aria-labelledby="leadership-george-heading"
                className="relative border-b border-[rgba(212,175,55,0.12)] bg-[#05070a]"
            >
                <div className="mx-auto grid max-w-[1200px] gap-10 px-6 py-20 md:grid-cols-[minmax(160px,200px)_1fr] md:gap-12 md:px-10 md:py-24">
                    {/* Headshot column — compact 187px max (22% smaller than 240px) */}
                    <div className="md:sticky md:top-24 md:self-start">
                        <div
                            data-testid="leadership-george-headshot"
                            className="relative mx-auto aspect-square w-[140px] overflow-hidden rounded-full border-2 border-[rgba(212,175,55,0.5)] bg-[#0b0e13] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)] md:w-full md:max-w-[187px]"
                        >
                            <img
                                src={GEORGE_HEADSHOT}
                                alt="Portrait of George Goldman, Founder and Principal of Endurance Casualty"
                                className="h-full w-full object-cover"
                                loading="eager"
                            />
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-brand-gold/40"
                            />
                        </div>
                        <div className="mt-4 text-center">
                            <p className="font-serif text-base leading-tight text-brand-ivory">
                                George Goldman, MBA, CFP&reg;
                            </p>
                            <p className="mt-1 text-[9px] uppercase tracking-[0.28em] text-brand-goldSoft">
                                Founder &amp; Principal
                            </p>
                        </div>
                    </div>

                    {/* Bio column */}
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                            Founder
                        </p>
                        <h2
                            id="leadership-george-heading"
                            className="mt-4 font-serif text-3xl leading-tight text-brand-ivory md:text-4xl"
                        >
                            George Goldman, MBA, CFP&reg;
                        </h2>
                        <div className="gold-hairline my-7 w-24" />

                        <div className="space-y-4 text-base leading-relaxed text-brand-ivory/85">
                            <p>
                                George Goldman, MBA, CFP&reg;, is the Founder
                                and Principal of {COMPANY.shortName}. With
                                more than 30 years of experience in the
                                insurance and financial services industry,
                                George has built his career on helping
                                individuals, families, and business owners
                                make informed decisions to protect what
                                matters most.
                            </p>
                            <p>
                                Throughout his career, George has worked
                                closely with clients and institutions to
                                identify coverage gaps, reduce insurance
                                costs where appropriate, and provide access
                                to highly rated insurance carriers and
                                specialized protection strategies. His
                                approach combines technical expertise with
                                personalized service, ensuring clients
                                receive solutions tailored to their unique
                                needs and objectives.
                            </p>
                            <p>
                                George founded {COMPANY.shortName} with a
                                simple mission: to provide trustworthy
                                guidance, exceptional service, and broad
                                market access to clients and institutions
                                seeking a better insurance experience.
                                Inspired by the principles of perseverance,
                                integrity, and commitment, the agency is
                                dedicated to building lasting relationships
                                and delivering meaningful value to every
                                client it serves.
                            </p>
                            <p>
                                Based on the East Coast, George lives with
                                his wife, Jing. He frequently travels to
                                meet clients and business partners in
                                person, believing that strong relationships
                                are built through direct communication,
                                personal attention, and genuine service.
                            </p>
                            <p>
                                At {COMPANY.shortName}, George remains
                                focused on providing knowledgeable advice,
                                responsive service, and dependable
                                protection for every stage of life and
                                business.
                            </p>
                        </div>

                        {/* Credential badges */}
                        <ul
                            data-testid="leadership-george-credentials"
                            className="mt-8 flex flex-wrap gap-3"
                            aria-label="Professional credentials"
                        >
                            {CREDENTIAL_BADGES.map(({ icon: Icon, label }) => (
                                <li
                                    key={label}
                                    className="inline-flex items-center gap-2 rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0b0e13] px-4 py-2 text-xs text-brand-ivory/90"
                                >
                                    <Icon
                                        aria-hidden="true"
                                        className="h-3.5 w-3.5 text-brand-gold2"
                                    />
                                    <span className="uppercase tracking-[0.18em]">
                                        {label}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        {/* Quick contact row */}
                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <a
                                href={`tel:${COMPANY.phoneTel}`}
                                data-testid="leadership-george-call"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg transition-colors hover:bg-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                            >
                                <Phone aria-hidden="true" className="h-3.5 w-3.5" />
                                Call {COMPANY.phone}
                            </a>
                            <a
                                href={`mailto:${COMPANY.email}`}
                                data-testid="leadership-george-email"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                            >
                                <Mail aria-hidden="true" className="h-3.5 w-3.5" />
                                {COMPANY.email}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============= OUR AGENTS — single elegant statement panel
                until real advisor profiles are ready to publish ============= */}
            <section
                data-testid="leadership-agents"
                aria-labelledby="leadership-agents-heading"
                className="relative border-b border-[rgba(212,175,55,0.12)] bg-[#06080c]"
            >
                <div className="mx-auto max-w-[1200px] px-6 py-20 md:px-10 md:py-24">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Our Insurance Professionals
                    </p>
                    <h2
                        id="leadership-agents-heading"
                        className="mt-4 font-serif text-3xl leading-tight text-brand-ivory md:text-4xl"
                    >
                        Meet Our Insurance Professionals
                    </h2>
                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-brand-muted">
                        Experienced advisors dedicated to helping clients
                        navigate today&apos;s increasingly complex insurance
                        marketplace.
                    </p>

                    {/* Quality-control statement panel — replaces the
                        previous 5 placeholder cards. Generic, name-free
                        compliance posture that doesn't change as advisors
                        join the team. */}
                    <div
                        data-testid="leadership-agents-statement"
                        className="mt-10 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-8 md:p-10"
                    >
                        <div className="flex items-center gap-3">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0d1117]">
                                <ShieldCheck
                                    aria-hidden="true"
                                    className="h-5 w-5 text-brand-gold2"
                                />
                            </span>
                            <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                Quality &amp; Compliance Standard
                            </p>
                        </div>
                        <p className="mt-5 max-w-3xl text-base leading-relaxed text-brand-ivory/85 md:text-lg">
                            Every client request submitted through this
                            website is reviewed by a licensed insurance
                            professional and matched to a producer with the
                            right jurisdictional authority and specialty for
                            the request. All inquiries are handled under
                            documented quality-control procedures designed
                            to ensure accurate guidance, prompt response,
                            and compliance with applicable insurance laws
                            and carrier requirements.
                        </p>
                        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-brand-muted">
                            See our{" "}
                            <Link
                                to="/licensing"
                                className="text-brand-gold2 underline-offset-4 hover:underline"
                            >
                                Licensing &amp; Disclosures
                            </Link>{" "}
                            page for the current state list and lines of
                            authority.
                        </p>
                    </div>

                    {/* Quiet recruiting funnel — licensed producers visiting
                        the page can apply via /contact?topic=agent-recruitment
                        which surfaces a pre-filled email pathway on Contact. */}
                    <div
                        data-testid="leadership-recruiting-cta"
                        className="mt-10 flex flex-col items-center gap-2 border-t border-[rgba(212,175,55,0.12)] pt-8 text-center"
                    >
                        <p className="text-[10px] uppercase tracking-[0.35em] text-brand-goldSoft">
                            Licensed Producers
                        </p>
                        <Link
                            to="/contact?topic=agent-recruitment"
                            data-testid="leadership-recruiting-link"
                            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-brand-gold2 underline-offset-4 hover:text-brand-gold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                        >
                            Apply to join our team
                            <ArrowRight aria-hidden="true" className="h-4 w-4" />
                        </Link>
                        <p className="max-w-md text-xs text-brand-muted">
                            Bring your state licenses, lines of authority,
                            and specialties. We&apos;ll respond personally.
                        </p>
                    </div>
                </div>
            </section>

            {/* ============= WHY CLIENTS CHOOSE ENDURANCE ============= */}
            <section
                data-testid="leadership-why"
                aria-labelledby="leadership-why-heading"
                className="relative border-b border-[rgba(212,175,55,0.12)] bg-[#05070a]"
            >
                <div className="mx-auto max-w-[1200px] px-6 py-20 md:px-10 md:py-24">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Why Clients Choose Endurance
                    </p>
                    <h2
                        id="leadership-why-heading"
                        className="mt-4 font-serif text-3xl leading-tight text-brand-ivory md:text-4xl"
                    >
                        Three commitments. Every client. Every time.
                    </h2>

                    <ul className="mt-12 grid gap-6 md:grid-cols-3 md:gap-8">
                        {WHY_PILLARS.map(({ icon: Icon, title, body }) => (
                            <li
                                key={title}
                                className="relative rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13] p-7 transition-colors hover:border-brand-gold/60"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(212,175,55,0.45)] bg-[#0d1117]">
                                    <Icon
                                        aria-hidden="true"
                                        className="h-5 w-5 text-brand-gold2"
                                    />
                                </div>
                                <h3 className="mt-6 font-serif text-xl text-brand-ivory">
                                    {title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                                    {body}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ============= CALL TO ACTION ============= */}
            <section
                data-testid="leadership-cta"
                aria-labelledby="leadership-cta-heading"
                className="relative overflow-hidden border-b border-[rgba(212,175,55,0.18)]"
            >
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(20,38,72,0.6),rgba(5,7,10,0)_65%),linear-gradient(180deg,#0a1426_0%,#070b14_55%,#05070a_100%)]"
                />
                <Compass
                    aria-hidden="true"
                    className="pointer-events-none absolute right-[-60px] top-[-60px] h-[280px] w-[280px] text-brand-gold opacity-[0.06]"
                    strokeWidth={0.6}
                />

                <div className="relative mx-auto max-w-[1200px] px-6 py-20 text-center md:px-10 md:py-28">
                    <p className="text-[11px] uppercase tracking-[0.45em] text-brand-goldSoft">
                        Get Started
                    </p>
                    <h2
                        id="leadership-cta-heading"
                        className="mx-auto mt-4 max-w-3xl font-serif text-3xl leading-tight text-brand-ivory md:text-4xl lg:text-5xl"
                    >
                        Ready to{" "}
                        <span className="text-brand-gold2">
                            Protect What You Have Built
                        </span>
                        ?
                    </h2>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-ivory/85 md:text-lg">
                        Whether you are seeking personal insurance, business
                        protection, employee benefits, life insurance, or
                        specialized coverage solutions, the{" "}
                        {COMPANY.shortName} team is ready to help.
                    </p>
                    <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Link
                            to="/#inquiry"
                            data-testid="leadership-cta-quote"
                            className="inline-flex items-center justify-center gap-3 rounded-full border border-brand-gold bg-brand-gold px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-brand-bg shadow-[0_18px_36px_-18px_rgba(212,175,55,0.6)] transition-all hover:bg-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg active:scale-[0.98]"
                        >
                            <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                            Request a Quote
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="leadership-cta-consult"
                            className="inline-flex items-center justify-center gap-3 rounded-full border border-brand-gold/60 bg-[#0b0e13] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                        >
                            <CalendarCheck
                                aria-hidden="true"
                                className="h-4 w-4"
                            />
                            Schedule a Consultation
                        </Link>
                    </div>
                </div>
            </section>

            <FooterBar />
        </main>
    );
}
